import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, CircularProgress,
} from "@mui/material";
import { format } from "date-fns";

import API from "../../utils/Axios.utils";
import { Notification } from "../../utils/Notification";
import logger from "../../utils/logger";
import useAuth from "../../hooks/useAuth";
import ReviewRequisitionModal from "../../components/Headcount/ReviewRequisitionModal";
import OfferReviewDialog from "./OfferReviewDialog";

// Kinds of items that can wait for a manager's decision, each with its own
// loader and review dialog.
export const APPROVAL_TYPES = Object.freeze({
  REQUISITION: "Requisition",
  OFFER: "Offer",
});

const PENDING_REQUISITION_STATUS = "pending_approval";
const PENDING_OFFER_STATUS = "Pending_Approval";

/** Map a pending requisition to an inbox row. */
const toRequisitionItem = (req) => ({
  key: `requisition-${req.id}`,
  type: APPROVAL_TYPES.REQUISITION,
  reference: req.requisition_id,
  title: req.role_title,
  detail: [req.client_name, req.department, req.no_of_people ? `${req.no_of_people} seat(s)` : null]
    .filter(Boolean)
    .join(" · "),
  requestedBy: req.creator_first_name
    ? `${req.creator_first_name} ${req.creator_last_name || ""}`.trim()
    : req.creator_email || "—",
  requestedAt: req.created_at,
  priority: req.priority,
  source: req,
});

/** Map an offer submitted for approval to an inbox row. */
const toOfferItem = (offer) => ({
  key: `offer-${offer.offer_id}`,
  type: APPROVAL_TYPES.OFFER,
  reference: offer.job?.job_code || `Offer ${offer.offer_id}`,
  title: offer.profile?.candidate_name || "Candidate",
  detail: [offer.job?.title, offer.compensation].filter(Boolean).join(" · "),
  requestedBy: offer.submitted_by_name || "—",
  requestedAt: offer.submitted_at || offer.updatedAt,
  priority: null,
  source: offer,
});

// Each kind of approval: where to load it from and how to turn it into rows.
const SOURCES = [
  {
    type: APPROVAL_TYPES.REQUISITION,
    load: async () => {
      const res = await API.get("/api/requisitions/all");
      return (res?.data?.requisitions || [])
        .filter((req) => req.status === PENDING_REQUISITION_STATUS)
        .map(toRequisitionItem);
    },
  },
  {
    type: APPROVAL_TYPES.OFFER,
    load: async () => {
      const res = await API.get("/api/offers");
      return (Array.isArray(res?.data) ? res.data : [])
        .filter((offer) => offer.status === PENDING_OFFER_STATUS)
        .map(toOfferItem);
    },
  },
];

/**
 * Manager's home: the approvals inbox — everything waiting for a manager
 * decision (requisitions and offers). Job assignments have their own page
 * (/job-assignments).
 */
const Approvals = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [alertType, setAlertType] = useState("");

  const notify = useCallback((message, type) => {
    setAlertContent(message);
    setAlertType(type);
    setAlert(true);
  }, []);

  // Load every kind of approval; one failing does not hide the others.
  const loadItems = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled(SOURCES.map((source) => source.load()));
    const loaded = [];
    const failed = [];
    results.forEach((result, i) => {
      const { type } = SOURCES[i];
      if (result.status === "fulfilled") {
        logger.info(`[Approvals] Loaded ${result.value.length} pending ${type.toLowerCase()}(s).`);
        loaded.push(...result.value);
      } else {
        const err = result.reason;
        logger.error(`[Approvals] Failed to load pending ${type.toLowerCase()}s — ${err?.response?.data?.message || err?.response?.data?.error || err?.message}`);
        failed.push(`${type.toLowerCase()}s`);
      }
    });
    setItems(loaded);
    if (failed.length) {
      notify(`Could not load pending ${failed.join(" and ")}. Please try again.`, "error");
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => new Date(a.requestedAt) - new Date(b.requestedAt)),
    [items]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>Approvals</Typography>
        {!loading && <Chip label={`${items.length} pending`} color={items.length ? "warning" : "default"} size="small" />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Requests waiting for your decision, oldest first.
      </Typography>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : sortedItems.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 6 }}>
            <Typography variant="h6" fontWeight={600}>All caught up</Typography>
            <Typography variant="body2" color="text.secondary">
              New requests will appear here.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Request</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Requested on</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedItems.map((item) => (
                  <TableRow key={item.key} hover>
                    <TableCell><Chip label={item.type} size="small" variant="outlined" /></TableCell>
                    <TableCell>{item.reference}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                      {item.detail && <Typography variant="caption" color="text.secondary">{item.detail}</Typography>}
                    </TableCell>
                    <TableCell>{item.requestedBy}</TableCell>
                    <TableCell>{item.requestedAt ? format(new Date(item.requestedAt), "dd MMM yyyy") : "—"}</TableCell>
                    <TableCell>
                      {item.priority && (
                        <Chip label={item.priority} size="small" color={item.priority === "High" ? "error" : "default"} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={() => setSelected(item)}>
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {selected?.type === APPROVAL_TYPES.REQUISITION && (
        <ReviewRequisitionModal
          open
          requisition={selected.source}
          currentUserEmail={(user?.email || "").toLowerCase()}
          onClose={() => setSelected(null)}
          onUpdated={() => {
            logger.info(`[Approvals] Decision recorded for ${selected.reference}.`);
            loadItems();
          }}
          setAlert={setAlert}
          setAlertContent={setAlertContent}
          setAlertType={setAlertType}
        />
      )}

      {selected?.type === APPROVAL_TYPES.OFFER && (
        <OfferReviewDialog
          offer={selected.source}
          onClose={() => setSelected(null)}
          onDecided={() => {
            logger.info(`[Approvals] Decision recorded for offer ${selected.source.offer_id}.`);
            loadItems();
          }}
          notify={notify}
        />
      )}

      <Notification open={alert} onClose={() => setAlert(false)} message={alertContent} severity={alertType} />
    </Box>
  );
};

export default Approvals;
